import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Print,
  Restaurant,
  LocalHospital,
  CalendarToday,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ipdAPI } from '../../services/api';

function ViewIPDPlans() {
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};

  const [patientDetails, setPatientDetails] = useState({
    patientCode: patientInfo.patientCode || '',
    patientName: patientInfo.patientName || '',
    roomNumber: patientInfo.roomNumber || '',
    admissionDate: patientInfo.admissionDate || '',
    plannedDays: patientInfo.plannedDays || 0,
    status: patientInfo.status || 'admitted',
    dischargeDate: patientInfo.dischargeDate || null
  });

  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dietPlans, setDietPlans] = useState([]);
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const printRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `IPD Plans - ${patientDetails.patientName || ''} - ${patientDetails.patientCode || ''}`,
    pageStyle: '@page { size: A4 portrait; margin: 10mm; }'
  });

  useEffect(() => {
    const fetchAdmissionDetails = async () => {
      try {
        const response = await ipdAPI.getAdmissionDetails(admissionId);
        const data = response.data.data || response.data;
        if (data) {
          setPatientDetails({
            patientCode: data.patient_code,
            patientName: `${data.first_name} ${data.last_name}`,
            roomNumber: data.room_number,
            admissionDate: data.admission_date,
            plannedDays: data.planned_days || 0,
            status: data.status,
            dischargeDate: data.discharge_date
          });
        }
      } catch (err) {
        console.error('Failed to load admission details:', err);
      }
    };

    fetchAdmissionDetails();
  }, [admissionId]);

  useEffect(() => {
    if (patientDetails.admissionDate) {
      const admission = new Date(patientDetails.admissionDate);
      setStartDate(formatDate(admission));

      if (patientDetails.status === 'discharged' && patientDetails.dischargeDate) {
        const discharge = new Date(patientDetails.dischargeDate);
        setEndDate(formatDate(discharge));
      } else if (patientDetails.plannedDays > 0) {
        const end = new Date(admission);
        end.setDate(end.getDate() + (patientDetails.plannedDays - 1));
        setEndDate(formatDate(end));
      } else {
        setEndDate(formatDate(new Date()));
      }
    }
  }, [patientDetails]);

  useEffect(() => {
    if (startDate && endDate) {
      loadPlans();
    }
  }, [startDate, endDate, admissionId]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadPlans = async () => {
    setLoading(true);
    setError('');
    try {
      const [dietResponse, treatmentResponse] = await Promise.all([
        ipdAPI.getDietPlan(admissionId, { startDate, endDate }),
        ipdAPI.getTreatmentPlan(admissionId, { startDate, endDate })
      ]);

      const dietData = dietResponse.data.data || dietResponse.data;
      const treatmentData = treatmentResponse.data.data || treatmentResponse.data;

      setDietPlans(Array.isArray(dietData) ? dietData : []);
      setTreatmentPlans(Array.isArray(treatmentData) ? treatmentData : []);
    } catch (err) {
      setError('Failed to load plans. Please try again.');
      console.error('Load plans error:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupPlansByDate = (plans, key) => {
    const grouped = {};
    plans.forEach(plan => {
      const date = plan[key];
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(plan);
    });
    return grouped;
  };

  const renderDietPlan = () => {
    if (dietPlans.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            No diet plans available for the selected date range
          </Typography>
        </Paper>
      );
    }

    const plansByDate = groupPlansByDate(dietPlans, 'planDate');
    const MEAL_TIMES = ['7AM', 'Breakfast', '11AM', 'Lunch', '5PM', 'Dinner'];

    return (
      <Box>
        {Object.entries(plansByDate).map(([date, plans]) => (
          <Paper key={date} sx={{ mb: 3, p: 3 }} elevation={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday />
              {new Date(date).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {MEAL_TIMES.map(mealTime => {
                const mealPlan = plans.find(p => p.mealTime === mealTime);
                return (
                  <Grid item xs={12} md={6} key={mealTime}>
                    <Paper sx={{ p: 2, bgcolor: mealPlan ? 'success.50' : 'grey.50', minHeight: 120 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {mealTime}
                        </Typography>
                        {mealPlan && (
                          <Chip label="Planned" color="success" size="small" />
                        )}
                      </Box>
                      {mealPlan ? (
                        <Box>
                          <Box sx={{ mb: 1 }}>
                            {mealPlan.items.map((item, idx) => (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Typography variant="body2">
                                  • {item.name}
                                  {item.quantity && ` - ${item.quantity}`}
                                </Typography>
                                {item.specialInstructions && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2, display: 'block' }}>
                                    ({item.specialInstructions})
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                          {mealPlan.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Notes: {mealPlan.notes}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not planned
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderTreatmentPlan = () => {
    if (treatmentPlans.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            No treatment plans available for the selected date range
          </Typography>
        </Paper>
      );
    }

    const plansByDate = groupPlansByDate(treatmentPlans, 'planDate');
    const TIME_SLOTS = ['Satkriya', 'Morning', 'Evening', 'Night'];

    return (
      <Box>
        {Object.entries(plansByDate).map(([date, plans]) => (
          <Paper key={date} sx={{ mb: 3, p: 3 }} elevation={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday />
              {new Date(date).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {TIME_SLOTS.map(timeSlot => {
                const treatmentPlan = plans.find(p => p.timeSlot === timeSlot);
                const icon = timeSlot === 'Satkriya' ? '🙏' : timeSlot === 'Morning' ? '🌅' : timeSlot === 'Evening' ? '🌙' : '🌃';
                return (
                  <Grid item xs={12} key={timeSlot}>
                    <Paper sx={{ p: 2, bgcolor: treatmentPlan ? 'secondary.50' : 'grey.50', minHeight: 100 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {icon} {timeSlot}
                        </Typography>
                        {treatmentPlan && (
                          <Chip label="Planned" color="secondary" size="small" />
                        )}
                      </Box>
                      {treatmentPlan ? (
                        <Box>
                          {treatmentPlan.treatments && treatmentPlan.treatments.length > 0 ? (
                            <Box sx={{ mb: 1 }}>
                              {treatmentPlan.treatments.map((treatment, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Typography variant="h6" color="secondary">
                                    • {treatment.treatmentName}
                                  </Typography>
                                  {treatment.treatmentDescription && (
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                      {treatment.treatmentDescription}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No treatments specified
                            </Typography>
                          )}
                          {treatmentPlan.instructions && (
                            <Paper sx={{ p: 1.5, bgcolor: 'white', mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                Instructions:
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {treatmentPlan.instructions}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not planned
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderCombinedPlan = () => {
    if (dietPlans.length === 0 && treatmentPlans.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            No plans available for the selected date range
          </Typography>
        </Paper>
      );
    }

    // Get all unique dates
    const allDates = new Set([
      ...dietPlans.map(p => p.planDate),
      ...treatmentPlans.map(p => p.planDate)
    ]);

    const sortedDates = Array.from(allDates).sort();

    return (
      <Box>
        {sortedDates.map(date => {
          const dateDietPlans = dietPlans.filter(p => p.planDate === date);
          const dateTreatmentPlans = treatmentPlans.filter(p => p.planDate === date);

          return (
            <Paper key={date} sx={{ mb: 3, p: 3 }} elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday />
                {new Date(date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Treatment Plan Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalHospital color="secondary" />
                  Treatment Schedule
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell width="20%"><strong>Time Slot</strong></TableCell>
                        <TableCell><strong>Treatment</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {['Morning', 'Afternoon', 'Evening'].map(slot => {
                        const plan = dateTreatmentPlans.find(p => p.timeSlot === slot);
                        const icon = slot === 'Morning' ? '🌅' : slot === 'Afternoon' ? '☀️' : '🌙';
                        return (
                          <TableRow key={slot}>
                            <TableCell>{icon} {slot}</TableCell>
                            <TableCell>
                              {plan ? (
                                <Box>
                                  {plan.treatments && plan.treatments.length > 0 ? (
                                    <Box>
                                      {plan.treatments.map((treatment, idx) => (
                                        <Typography key={idx} variant="body2" fontWeight="bold">
                                          • {treatment.treatmentName}
                                        </Typography>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No treatments specified
                                    </Typography>
                                  )}
                                  {plan.instructions && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {plan.instructions}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                  Not planned
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Diet Plan Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Restaurant color="success" />
                  Diet Schedule
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell width="15%"><strong>Meal Time</strong></TableCell>
                        <TableCell><strong>Food Items</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {['7AM', '9AM', '11AM', '1PM', '3PM', '6PM'].map(mealTime => {
                        const plan = dateDietPlans.find(p => p.mealTime === mealTime);
                        return (
                          <TableRow key={mealTime}>
                            <TableCell>{mealTime}</TableCell>
                            <TableCell>
                              {plan ? (
                                <Box>
                                  {plan.items.map((item, idx) => (
                                    <Typography key={idx} variant="body2">
                                      • {item.name}
                                      {item.quantity && ` (${item.quantity})`}
                                    </Typography>
                                  ))}
                                  {plan.notes && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                      Notes: {plan.notes}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                  Not planned
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  // Build sorted list of all dates with their plans
  const allDates = Array.from(new Set([
    ...dietPlans.map(p => p.planDate),
    ...treatmentPlans.map(p => p.planDate)
  ])).sort();

  return (
    <Box>
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/reception')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight="bold">IPD Treatment & Diet Plans</Typography>
            </Box>
            <Button variant="contained" startIcon={<Print />} onClick={handlePrint}
              disabled={dietPlans.length === 0 && treatmentPlans.length === 0}>
              Print
            </Button>
          </Box>

          {/* Patient info */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Patient Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Patient Code</Typography>
                <Typography variant="body1" fontWeight="medium">{patientDetails.patientCode || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Patient Name</Typography>
                <Typography variant="body1" fontWeight="medium">{patientDetails.patientName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Room</Typography>
                <Typography variant="body1" fontWeight="medium">{patientDetails.roomNumber || 'N/A'}</Typography>
              </Grid>
              {patientDetails.admissionDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Admission Date</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(patientDetails.admissionDate).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Date range + load */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="date" label="Start Date" value={startDate}
                  onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="date" label="End Date" value={endDate}
                  onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button fullWidth variant="outlined" onClick={loadPlans} disabled={!startDate || !endDate}>
                  Load Plans
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
              <Tab icon={<Restaurant />} iconPosition="start" label="Diet Plan" />
              <Tab icon={<LocalHospital />} iconPosition="start" label="Treatment Plan" />
              <Tab label="Combined View" />
            </Tabs>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <Box>
              {activeTab === 0 && renderDietPlan()}
              {activeTab === 1 && renderTreatmentPlan()}
              {activeTab === 2 && renderCombinedPlan()}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Off-screen print portal */}
      {createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '190mm' }}>
          <div ref={printRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center', padding: '0 8px' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>IPD TREATMENT & DIET PLAN</div>
              </div>
              <div style={{ fontSize: 9, color: '#888', textAlign: 'right' }}>
                Printed: {new Date().toLocaleString('en-IN')}
              </div>
            </div>

            {/* Patient info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '3px 16px', marginBottom: 12, fontSize: 10, background: '#f1f8e9', padding: '6px 8px', borderRadius: 4, border: '1px solid #c5e1a5' }}>
              <div><strong>Patient:</strong> {patientDetails.patientName || 'N/A'}</div>
              <div><strong>UHID:</strong> {patientDetails.patientCode || 'N/A'}</div>
              <div><strong>Room:</strong> {patientDetails.roomNumber || 'N/A'}</div>
              <div><strong>Admission:</strong> {patientDetails.admissionDate ? new Date(patientDetails.admissionDate).toLocaleDateString('en-GB') : 'N/A'}</div>
              <div><strong>Period:</strong> {startDate} to {endDate}</div>
              {patientDetails.plannedDays > 0 && <div><strong>Planned Days:</strong> {patientDetails.plannedDays}</div>}
            </div>

            {/* One section per date */}
            {allDates.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>No plans available for selected date range.</div>
            ) : allDates.map((date, di) => {
              const dateDiet = dietPlans.filter(p => p.planDate === date);
              const dateTreatment = treatmentPlans.filter(p => p.planDate === date);
              const dateLabel = new Date(date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

              return (
                <div key={date} style={{ marginBottom: 16, pageBreakInside: 'avoid' }}>
                  {/* Date heading */}
                  <div style={{ background: '#1b5e20', color: '#fff', padding: '4px 10px', fontWeight: 700, fontSize: 11, marginBottom: 6, borderRadius: 2 }}>
                    {dateLabel}
                  </div>

                  {/* Treatment plan */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ background: '#e8f5e9', padding: '3px 8px', fontWeight: 700, fontSize: 10, borderLeft: '3px solid #2e7d32', marginBottom: 3 }}>
                      Treatment Schedule
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', width: '15%', textAlign: 'left' }}>Time Slot</th>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>Treatments</th>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', width: '30%', textAlign: 'left' }}>Instructions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {['Satkriya', 'Morning', 'Evening', 'Night'].map((slot, si) => {
                          const plan = dateTreatment.find(p => p.timeSlot === slot);
                          const icon = slot === 'Satkriya' ? '🙏' : slot === 'Morning' ? '🌅' : slot === 'Evening' ? '🌙' : '🌃';
                          return (
                            <tr key={slot} style={{ background: si % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>{icon} {slot}</td>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>
                                {plan && plan.treatments && plan.treatments.length > 0
                                  ? plan.treatments.map((t, i) => <div key={i}>• {t.treatmentName}</div>)
                                  : <span style={{ color: '#aaa', fontStyle: 'italic' }}>Not planned</span>}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px', color: '#555' }}>
                                {plan?.instructions || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Diet plan */}
                  <div>
                    <div style={{ background: '#fff8e1', padding: '3px 8px', fontWeight: 700, fontSize: 10, borderLeft: '3px solid #f57f17', marginBottom: 3 }}>
                      Diet Schedule
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', width: '12%', textAlign: 'left' }}>Meal Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>Food Items</th>
                          <th style={{ border: '1px solid #ddd', padding: '3px 6px', width: '25%', textAlign: 'left' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {['7AM', 'Breakfast', '11AM', 'Lunch', '5PM', 'Dinner'].map((mealTime, mi) => {
                          const mealLabels = { '7AM': 'Early Morning', 'Breakfast': 'Breakfast', '11AM': 'Mid Morning', 'Lunch': 'Lunch', '5PM': 'Evening', 'Dinner': 'Dinner' };
                          const plan = dateDiet.find(p => p.mealTime === mealTime);
                          return (
                            <tr key={mealTime} style={{ background: mi % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>
                                {mealTime}<br /><span style={{ fontWeight: 400, fontSize: 9, color: '#666' }}>{mealLabels[mealTime]}</span>
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>
                                {plan && plan.items && plan.items.length > 0
                                  ? plan.items.map((item, i) => (
                                      <div key={i}>• {item.name}{item.quantity ? ` (${item.quantity})` : ''}{item.specialInstructions ? <span style={{ color: '#777', fontSize: 9 }}> — {item.specialInstructions}</span> : ''}</div>
                                    ))
                                  : <span style={{ color: '#aaa', fontStyle: 'italic' }}>Not planned</span>}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '3px 6px', color: '#555' }}>
                                {plan?.notes || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {di < allDates.length - 1 && (
                    <div style={{ borderBottom: '1px dashed #ccc', marginTop: 12 }} />
                  )}
                </div>
              );
            })}

            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 6, textAlign: 'center', fontSize: 9, color: '#888' }}>
              MSG Elite One World Wellness — Sirsa, Haryana
            </div>
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}

export default ViewIPDPlans;
